import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

export default function CampaignToggle({
  apiEndpoint = "https://orp6yaxiago7jck4enpo6jikam0uyvxl.lambda-url.us-east-1.on.aws/campaign_enable",
  statusUrl = "https://orp6yaxiago7jck4enpo6jikam0uyvxl.lambda-url.us-east-1.on.aws/",
  configName = "campaign_enable",
}) {
  const [campaignActive, setCampaignActive] = useState(false);
  const [statusText, setStatusText] = useState("Cargando estado...");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // -------- GET: cargar estado actual desde statusUrl --------
  const loadStatus = async () => {
    try {
      setIsFetching(true);
      setError(null);
      setStatusText("Cargando estado...");

      const res = await fetch(statusUrl, {
        method: "GET",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) throw new Error(`Error GET estado: ${res.status}`);

      const data = await res.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      const item = items.find((it) => it?.config_name === configName);

      if (!item) throw new Error(`Config '${configName}' no encontrada en items`);

      // Prioriza "estado"; si no existe, usa "valor"
      const estado = typeof item.estado !== "undefined" ? item.estado : item.valor;
      const active = Boolean(estado);

      setCampaignActive(active);
      setStatusText(active ? "Campaña Activa" : "Campaña Inactiva");
      setLastUpdated(new Date());
    } catch (e) {
      console.error(e);
      setError(e.message);
      setStatusText(`Error: ${e.message}`);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusUrl, configName]);

  // -------- POST: alternar estado (tu lógica) --------
  const toggleCampaign = async () => {
    const newState = !campaignActive;
    setIsLoading(true);
    setStatusText(newState ? "Activando campaña..." : "Desactivando campaña...");
    setError(null);

    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ estado: newState }),
      });

      if (!response.ok) throw new Error(`Error en la petición: ${response.status}`);

      const data = await response.json();
      const estado = Boolean(data?.item?.estado);

      setCampaignActive(estado);
      setStatusText(estado ? "Campaña Activa" : "Campaña Inactiva");
      setLastUpdated(new Date());
      console.log(`Campaña ${estado ? "activada" : "desactivada"} correctamente`);
    } catch (err) {
      console.error("Error al cambiar estado de campaña:", err);
      setError(err.message);
      setStatusText(`Error: ${err.message}`);
      // Revertir el estado visual si falla
      setCampaignActive(!newState);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="campaign-toggle-container">
      <div className="toggle-header">
        <h3>Estado de Campaña</h3>
        <p className={`status-text ${error ? "error" : ""} ${isLoading || isFetching ? "loading" : ""}`}>
          {statusText}
          {lastUpdated && !isLoading && !isFetching && (
            <span className="last-updated">
              {` · Actualizado: ${lastUpdated.toLocaleTimeString()}`}
            </span>
          )}
        </p>
      </div>

      <div className="actions">
        <div
          className={`toggle-switch ${campaignActive ? "active" : ""} ${isLoading ? "disabled" : ""}`}
          onClick={!isLoading ? toggleCampaign : undefined}
          role="switch"
          aria-checked={campaignActive}
          aria-label={campaignActive ? "Desactivar campaña" : "Activar campaña"}
          aria-busy={isLoading}
        >
          <span className="toggle-label">OFF</span>
          <div className="toggle-knob">
            {isLoading ? <span className="spinner"></span> : <span className="toggle-icon">{campaignActive ? "✓" : "✕"}</span>}
          </div>
          <span className="toggle-label">ON</span>
        </div>
      </div>

      {error && (
        <div className="error-message" role="alert">
          <span className="error-icon">⚠️</span> {error}
        </div>
      )}

      <style jsx>{`
        .campaign-toggle-container {
          background: linear-gradient(135deg, #f6f9fc 0%, #edf2f7 100%);
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 10px 25px rgba(101, 119, 255, 0.2), 0 5px 10px rgba(101, 119, 255, 0.1);
          max-width: 480px;
          margin: 0 auto;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
          border: 1px solid #e2e8f0;
        }
        
        .toggle-header {
          margin-bottom: 24px;
          text-align: center;
        }
        
        .toggle-header h3 {
          margin: 0 0 12px 0;
          color: #2d3748;
          font-size: 22px;
          font-weight: 700;
          letter-spacing: 0.5px;
        }
        
        .status-text {
          margin: 0;
          font-size: 16px;
          color: #6574cd;
          display: flex;
          flex-direction: column;
          align-items: center;
          font-weight: 500;
        }
        
        .status-text.loading {
          color: #5a67d8;
        }
        
        .status-text.error {
          color: #e53e3e;
        }
        
        .last-updated {
          font-size: 13px;
          color: #718096;
          margin-top: 6px;
          font-weight: 400;
        }
        
        .actions {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;
        }
        
        .toggle-switch {
          position: relative;
          display: flex;
          align-items: center;
          background: #cbd5e0;
          border-radius: 28px;
          padding: 6px;
          width: 160px;
          height: 52px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: inset 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .toggle-switch.active {
          background: linear-gradient(90deg, #6574cd 0%, #766cb3 100%);
        }
        
        .toggle-switch.disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .toggle-label {
          font-size: 14px;
          font-weight: 700;
          padding: 0 12px;
          color: #718096;
          z-index: 1;
          transition: color 0.3s ease;
        }
        
        .toggle-switch.active .toggle-label:first-child {
          color: rgba(255, 255, 255, 0.7);
        }
        
        .toggle-switch:not(.active) .toggle-label:last-child {
          color: rgba(255, 255, 255, 0.7);
        }
        
        .toggle-switch.active .toggle-label:last-child {
          color: #fff;
        }
        
        .toggle-knob {
          position: absolute;
          left: 6px;
          background: #fff;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s cubic-bezier(0.68, -0.55, 0.27, 1.55);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .toggle-switch.active .toggle-knob {
          left: calc(100% - 46px);
          transform: rotate(360deg);
        }
        
        .toggle-icon {
          font-size: 16px;
          color: #6574cd;
          font-weight: bold;
        }
        
        .toggle-switch.active .toggle-icon {
          color: #6574cd;
        }
        
        .spinner {
          display: inline-block;
          width: 18px;
          height: 18px;
          border: 2px solid rgba(101, 116, 205, 0.3);
          border-radius: 50%;
          border-top-color: #6574cd;
          animation: spin 0.8s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .error-message {
          background: #fed7d7;
          color: #c53030;
          padding: 12px 16px;
          border-radius: 8px;
          font-size: 14px;
          display: flex;
          align-items: center;
          margin-top: 16px;
          border-left: 4px solid #f56565;
        }
        
        .error-icon {
          margin-right: 10px;
          font-size: 18px;
        }
      `}</style>
    </div>
  );
}

CampaignToggle.propTypes = {
  apiEndpoint: PropTypes.string, // POST
  statusUrl: PropTypes.string,   // GET
  configName: PropTypes.string,  // clave dentro de items (default: 'campaign_enable')
};